import React, { useState } from 'react';
import { Row, Col, Tab, Tabs } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import ModalSidebar from '../../../../components/ModalSidebar';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { ApprovalRequestTableItem, mapApprovalRequests } from '../mappings';
import { ApprovalRequestStatus } from '../../../../__generated__/graphql';
import ApprovalRequestStatusCell from '../ApprovalRequestStatusCell';
import FormattedDateTime from '../../../../../../components/datetime/FormattedDateTime';
import { ReviewActionsCell } from '../ReviewActionsCell';
import { ExpenseDetails } from './ExpenseDetails';
import { EmployeeWithAvatar } from '../../../../components/Employee';
import { ChangeRequestDetails } from '../../../../manager/MyTeam/MyTeamChangeRequests/ChangeRequestsList/ChangeRequestPreview/ChangeRequestDetails';
import { AbsenceDetails } from './AbsenceDetails';
import CommentListWrapper from '../../../../components/Comment/CommentListWrapper/CommentListWrapper';
import { ApprovalPath } from '../../ApprovalPath';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useApprovalRequestByIdQuery } from '../../../../__generated__/graphql';
import { getCommentSupportedObjectName } from '../../../../components/Comment/types';
import {
  SalesforceObjectName,
  getRelatedObjectNameWithFallback,
} from '../../../../../../utils/objectTypes';
import FlairCard from '../../../../../../atomic/templates/FlairCard/FlairCard';
import { EmployeeDocumentDetails } from './EmployeeDocumentDetails';
import { recruitmentRoutes } from '../../../../recruitment/routes';

type Props = {
  show: boolean;
  onClose: () => void;
  approvalRequestId?: string;

  //Note: the side panel is used even when there are no approval request, this is why we need this param
  relatedRecordId?: string;
  relatedObjectNameStr?: string;
};

const i18Path = 'approvalRequest.detailsSidebar';

const tabKeys = {
  INFORMATION: 'information',
  COMMENTS: 'comments',
  APPROVAL_PATH: 'approvalPath',
};

type ContentProps = {
  approvalRequest?: Maybe<ApprovalRequestTableItem>;
  relatedRecordId: string;
  relatedObjectName: SalesforceObjectName;
};

const Content: React.FC<ContentProps> = ({
  approvalRequest,
  relatedRecordId,
  relatedObjectName,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const history = useHistory();

  const renderConditionalContent = () => {
    switch (relatedObjectName) {
      case 'flair__Expense__c':
        return <ExpenseDetails expenseId={relatedRecordId} />;
      case 'flair__Employee_Data_Change_Request__c':
        if (!approvalRequest) return null;
        return (
          <ChangeRequestDetails
            changeRequest={{
              id: approvalRequest.relatedRecordId,
              employeeId: approvalRequest.employee?.id || '',
            }}
          />
        );
      case 'flair__Absence__c':
        return <AbsenceDetails absenceId={relatedRecordId} />;
      case 'flair__Employee_Document__c':
        if (!approvalRequest) return null;
        return (
          <EmployeeDocumentDetails
            employeeId={approvalRequest.employee.id}
            documentId={relatedRecordId}
          />
        );
      case 'flair__Expense_Group__c':
        if (!approvalRequest) return null;
        return (
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => {
              history.push(
                `../manager/expenses?expanded=${approvalRequest.relatedRecordId}#${approvalRequest.relatedRecordId}`,
              );
            }}>
            {t('viewDetails')}
          </button>
        );
      case 'flair__Job__c':
        if (!approvalRequest) return null;
        return (
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => {
              history.push(
                recruitmentRoutes.jobDetails.route.create({
                  jobId: approvalRequest.relatedRecordId,
                }),
              );
            }}>
            {t('viewDetails')}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4">
      {/* Common Header Section */}
      {approvalRequest && (
        <div className="border-bottom pb-3 mb-3">
          <Row className="mb-3">
            <Col sm={6}>
              <div className="mb-2">
                <small className="text-muted d-block">{t('recordType')}</small>
                <span>{approvalRequest.relatedRecordObjectName}</span>
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-2">
                <small className="text-muted d-block">{t('recordName')}</small>
                <span>{approvalRequest.relatedRecordName}</span>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={6}>
              <div className="mb-2">
                <small className="text-muted d-block">{t('employee')}</small>
                <EmployeeWithAvatar employee={approvalRequest.employee} />
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-2">
                <small className="text-muted d-block">{t('requestedAt')}</small>
                <FormattedDateTime
                  dateTime={approvalRequest.requestedAt}
                  format="long"
                />
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={6}>
              <div className="mb-2">
                <small className="text-muted d-block">
                  {t('approverStatus')}
                </small>
                <ApprovalRequestStatusCell
                  status={approvalRequest.approvalStatus}
                />
                {approvalRequest.actionDatetime &&
                  [
                    ApprovalRequestStatus.Approved,
                    ApprovalRequestStatus.Rejected,
                  ].includes(approvalRequest.approvalStatus) && (
                    <FormattedDateTime
                      dateTime={approvalRequest.actionDatetime}
                      format="long"
                    />
                  )}
              </div>
            </Col>
          </Row>
          {approvalRequest.approverComment && (
            <Row className="mb-3">
              <Col>
                <div className="mb-2">
                  <small className="text-muted d-block">
                    {t('approverComment')}
                  </small>
                  <span>{approvalRequest.approverComment}</span>
                </div>
              </Col>
            </Row>
          )}
        </div>
      )}

      {approvalRequest && (
        <div className="my-4">
          {/* Approve/Reject Actions */}
          {approvalRequest.approvalStatus === ApprovalRequestStatus.Pending && (
            <Row>
              <Col>
                <div className="d-flex gap-2">
                  <ReviewActionsCell
                    id={approvalRequest.id}
                    relatedRecordId={approvalRequest.relatedRecordId}
                  />
                </div>
              </Col>
            </Row>
          )}
        </div>
      )}

      {/* Conditional Content Based on Record Type */}
      {renderConditionalContent()}
    </div>
  );
};

export const ApprovalRequestDetailsSidebar: React.FC<Props> = ({
  show,
  onClose,
  approvalRequestId,
  relatedRecordId: initialRelatedRecordId,
  relatedObjectNameStr: initialRelatedObjectNameStr,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const [selectedTabKey, setSelectedTabKey] = useState<string | null>(
    tabKeys.INFORMATION,
  );

  const { data: approvalRequestData } = useApprovalRequestByIdQuery({
    variables: { id: approvalRequestId || '' },
    skip: !approvalRequestId,
  });

  const approvalRequest = approvalRequestData?.approvalRequest
    ? mapApprovalRequests([approvalRequestData?.approvalRequest])[0]
    : null;
  const relatedRecordId =
    approvalRequest?.relatedRecordId || initialRelatedRecordId;
  const relatedObjectNameStr =
    approvalRequest?.relatedRecordApiName || initialRelatedObjectNameStr;

  if (!relatedRecordId || !relatedObjectNameStr) {
    return null;
  }

  const isSelectedTab = (tabKey: string) => tabKey === selectedTabKey;

  const relatedObjectName = getRelatedObjectNameWithFallback(
    relatedObjectNameStr || '',
  );

  return (
    <ModalSidebar
      show={show}
      onClose={onClose}
      header={approvalRequest ? t('header') : t('absenceDetailsHeader')}
      content={
        <Tabs
          activeKey={selectedTabKey ?? tabKeys.INFORMATION}
          className="mx-4"
          onSelect={setSelectedTabKey}>
          <Tab eventKey={tabKeys.INFORMATION} title={t('tabs.information')}>
            {isSelectedTab(tabKeys.INFORMATION) && (
              <Content
                approvalRequest={approvalRequest}
                relatedRecordId={relatedRecordId}
                relatedObjectName={relatedObjectName}
              />
            )}
          </Tab>
          <Tab
            eventKey={tabKeys.COMMENTS}
            title={t('tabs.comments')}
            className="h-100 comments-tab">
            {isSelectedTab(tabKeys.COMMENTS) && relatedRecordId && (
              <CommentListWrapper
                recordId={relatedRecordId}
                relatedObjectName={getCommentSupportedObjectName(
                  relatedObjectNameStr,
                )}
              />
            )}
          </Tab>
          <Tab
            eventKey={tabKeys.APPROVAL_PATH}
            title={t('tabs.approvalPath')}
            className="h-100 p-5">
            {isSelectedTab(tabKeys.APPROVAL_PATH) && (
              <FlairCard cardContainerClassName="rounded-3 pt-4">
                <ApprovalPath relatedRecordId={relatedRecordId} />
              </FlairCard>
            )}
          </Tab>
        </Tabs>
      }
    />
  );
};
